import { UploadFile } from 'antd';

import { ODOMETER_OPTIONS } from '@/constants/sell-your-car/sell-your-car.constants';

import { uploadPhotoToDirectus } from '../actions';
import { SellYourFormValues } from '../service';

export interface FormSubmissionData {
  user_name: string;
  email: string;
  phone_number: string;
  vehicle_year: string;
  vehicle_brand: string;
  vehicle_range?: string;
  vehicle_model: string;
  vehicle_fuel: string;
  vehicle_transmission: string;
  vehicle_kms: string | null;
  vehicle_state: string;
  photos: UploadFile[];
  message?: string;
  vehicle_expected_price: string;
  province: string;
}

export interface SubmissionResult {
  success: boolean;
  message: string;
  error?: string;
}

export async function processUploadedFiles(
  files: UploadFile[],
): Promise<string[]> {
  if (!files || files.length === 0) return [];

  const uploadPromises = files.map(async (file) => {
    if (file.originFileObj) {
      const formData = new FormData();

      formData.append('file', file.originFileObj);

      try {
        const uploadResult = await uploadPhotoToDirectus(formData);

        if (uploadResult.success) {
          return uploadResult.fileId;
        }
      } catch (error) {
        console.error('Failed to upload file:', error);
      }
    }

    if (file.response?.data?.id) {
      return file.response.data.id;
    }

    if (file.uid && !file.originFileObj) {
      return file.uid;
    }

    return null;
  });

  const results = await Promise.all(uploadPromises);

  return results.filter((id): id is string => id !== null);
}

export function createSubmissionPayload(
  data: FormSubmissionData,
  photoIds: string[],
) {
  const currentYear = new Date().getFullYear();

  return {
    user_name: data.user_name || '',
    email: data.email || '',
    phone_number: data.phone_number || '',
    province: data.province || '',
    vehicle_year: data.vehicle_year
      ? parseInt(data.vehicle_year, 10)
      : currentYear,
    vehicle_brand: data.vehicle_brand || '',
    vehicle_range: data.vehicle_range || '',
    vehicle_model: data.vehicle_model || '',
    message: data.message || '',
    vehicle_fuel: data.vehicle_fuel || '',
    vehicle_transmission: data.vehicle_transmission || '',
    vehicle_kms: data.vehicle_kms
      ? data.vehicle_kms
      : ODOMETER_OPTIONS[0]?.value || '0',
    vehicle_state: data.vehicle_state || '',
    vehicle_expected_price: data.vehicle_expected_price
      ? parseInt(String(data.vehicle_expected_price).replace(/[^0-9]/g, ''), 10)
      : 0,
    photoIds:
      photoIds.filter((id): id is string => typeof id === 'string') || [],
  };
}

export function validateRequiredFields(data: FormSubmissionData): string[] {
  const requiredFields = [
    'user_name',
    'email',
    'phone_number',
    'province',
    'vehicle_year',
    'vehicle_brand',
    'vehicle_fuel',
    'vehicle_transmission',
    'vehicle_kms',
    'vehicle_state',
  ] as const;

  return requiredFields.filter((field) => !data[field]);
}

export async function submitFormData(
  data: SellYourFormValues,
): Promise<SubmissionResult> {
  try {
    const missingFields = validateRequiredFields(data);

    if (missingFields.length > 0) {
      return {
        success: false,
        message: 'Missing required fields',
        error: `Please fill out all required fields: ${missingFields.join(', ')}`,
      };
    }

    const photoIds = await processUploadedFiles(data.photos);
    const formDataForSubmission = createSubmissionPayload(data, photoIds);

    const response = await fetch('/api/submit-lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formDataForSubmission),
    });

    return await response.json();
  } catch (error: any) {
    console.error('Error submitting form data:', error);
    return {
      success: false,
      message: 'Submission failed',
      error: error?.message || 'An unknown error occurred',
    };
  }
}
